/**
 * Insight Detail Dialog
 * =====================
 * Full modal showing comprehensive details about an AI insight
 * Helps teams determine if it's an anomaly or real issue to fix
 */

import React, { useState } from 'react';
import { X, AlertTriangle, TrendingUp, Target, DollarSign, Info, ExternalLink, CheckCircle, Brain, BarChart3, Loader2, Star } from 'lucide-react';
import { useLocation } from 'wouter';

interface Insight {
  id: string;
  insight_type: string;
  category: string;
  priority: string;
  title: string;
  description: string;
  recommendation: string;
  dollar_impact: number;
  confidence_score: number;
  source: string;
  retailer_id?: string;
  related_sku?: string;
  fiscal_week?: string;
  generated_at?: string;
  is_acknowledged?: boolean;
  metadata?: {
    model_name?: string;
    model_accuracy?: string;
  };
}

interface InsightDetailDialogProps {
  insight: Insight;
  onClose: () => void;
  retailerName?: string;
  onAcknowledge?: (insightId: string) => void;
}

export default function InsightDetailDialog({ insight, onClose, retailerName, onAcknowledge }: InsightDetailDialogProps) {
  const [, setLocation] = useLocation();
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [acknowledged, setAcknowledged] = useState(insight.is_acknowledged || false);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const getPriorityColor = (priority: string, insightType?: string) => {
    // Achievements get green celebratory treatment
    if (insightType === 'achievement') {
      return { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-900', badge: 'bg-green-600' };
    }
    
    switch (priority) {
      case 'critical': return { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-900', badge: 'bg-red-600' };
      case 'high': return { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-900', badge: 'bg-orange-600' };
      case 'medium': return { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-900', badge: 'bg-yellow-600' };
      default: return { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-900', badge: 'bg-blue-600' };
    }
  };

  const isAchievement = insight.insight_type === 'achievement';
  const colors = getPriorityColor(insight.priority, insight.insight_type);

  const isMLPowered = insight.source && (
    insight.source.includes('_ml') || 
    insight.source === 'stockout_risk_ml' || 
    insight.source === 'forecast_anomaly_ml' || 
    insight.source === 'cluster_opportunity_ml'
  );

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'stockout': return <AlertTriangle className="w-5 h-5" />;
      case 'inventory': return <AlertTriangle className="w-5 h-5" />;
      case 'revenue': return <DollarSign className="w-5 h-5" />;
      case 'performance': return <TrendingUp className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const getBusinessContext = () => {
    const contexts = [];
    
    // Achievement insights get celebratory context
    if (insight.insight_type === 'achievement') {
      contexts.push({
        title: '🎉 Celebrating Success',
        description: 'This SKU is performing exceptionally well! Strong sales indicate product-market fit and effective merchandising.',
        impact: 'Keep the momentum going with adequate inventory and continued support'
      });
    }
    // Category-specific context
    else if (insight.category === 'stockout' || insight.category === 'inventory') {
      contexts.push({
        title: 'Why This Matters',
        description: 'Stockouts directly impact revenue and customer satisfaction. Lost sales are often permanent as customers may switch to competitors.',
        impact: 'Immediate revenue loss + long-term customer retention risk'
      });
    } else if (insight.category === 'revenue') {
      if (insight.insight_type === 'opportunity') {
        contexts.push({
          title: 'Growth Opportunity',
          description: 'Week-over-week revenue growth indicates increasing demand or successful merchandising. Capitalize on momentum with adequate inventory.',
          impact: 'Potential to capture additional market share'
        });
      } else {
        contexts.push({
          title: 'Revenue Decline Alert',
          description: 'Revenue drops may indicate competitive pressure, pricing issues, or reduced demand. Early detection enables corrective action.',
          impact: 'Prevents further revenue erosion if addressed quickly'
        });
      }
    }
    
    // Confidence-based context
    if (insight.confidence_score >= 85) {
      contexts.push({
        title: 'High Confidence',
        description: 'This insight is based on strong data signals with consistent patterns. Recommended to act on this.',
        impact: `${Math.round(insight.confidence_score)}% confidence in this recommendation`
      });
    } else if (insight.confidence_score < 70) {
      contexts.push({
        title: 'Monitor Closely',
        description: 'Lower confidence suggests data may be limited or volatile. Validate with additional analysis before major decisions.',
        impact: 'Consider as early warning signal requiring verification'
      });
    }
    
    return contexts;
  };

  const handleViewSKU = () => {
    if (insight.related_sku) {
      setLocation(`/products?search=${encodeURIComponent(insight.related_sku)}`);
      onClose();
    }
  };

  const handleViewRetailer = () => {
    if (retailerName) {
      setLocation(`/dashboard?retailer=${retailerName.toLowerCase()}`);
      onClose();
    }
  };

  const handleAcknowledge = async () => {
    if (!insight.retailer_id) {
      console.error('[InsightDetailDialog] No retailer_id found');
      return;
    }

    try {
      setIsAcknowledging(true);
      
      const response = await fetch('/api/insights/acknowledge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          insight_id: insight.id,
          retailer_id: insight.retailer_id,
          notes: null
        })
      });

      const data = await response.json();

      if (data.success) {
        setAcknowledged(true);
        console.log('[InsightDetailDialog] Successfully acknowledged insight:', insight.id);
        
        // Notify parent component
        if (onAcknowledge) {
          onAcknowledge(insight.id);
        }
        
        // Close dialog after brief delay to show success state
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        console.error('[InsightDetailDialog] Failed to acknowledge:', data);
      }
    } catch (error) {
      console.error('[InsightDetailDialog] Error acknowledging insight:', error);
    } finally {
      setIsAcknowledging(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`sticky top-0 border-b-2 ${colors.border} ${colors.bg} p-6`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-3 py-1 ${colors.badge} text-white text-xs font-bold rounded-full uppercase flex items-center gap-1`}>
                  {isAchievement && <Star className="w-3 h-3" />}
                  {isAchievement ? 'WIN' : insight.priority}
                </span>
                {isMLPowered && (
                  <span className="px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full flex items-center gap-1">
                    <Brain className="w-3 h-3" />
                    Analytics-Powered
                  </span>
                )}
                {!isMLPowered && (
                  <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center gap-1">
                    <BarChart3 className="w-3 h-3" />
                    Data-Driven
                  </span>
                )}
                {retailerName && (
                  <span className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded-full">
                    {retailerName}
                  </span>
                )}
              </div>
              <h2 className={`text-2xl font-bold ${colors.text} mb-1`}>
                {insight.title}
              </h2>
              <p className="text-sm text-gray-600">
                Week {insight.fiscal_week} • {insight.category.charAt(0).toUpperCase() + insight.category.slice(1)}
                {insight.related_sku && ` • SKU: ${insight.related_sku}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Dollar Impact */}
          {insight.dollar_impact > 0 && (
            <div className="mt-4 pt-4 border-t border-current/20">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {isAchievement ? 'Weekly Revenue Generated' : 'Estimated Weekly Impact'}
                </span>
                <span className={`text-2xl font-bold ${colors.text}`}>
                  {formatCurrency(insight.dollar_impact)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          
          {/* Main Description */}
          <div className={`rounded-lg p-4 border-2 ${colors.border} ${colors.bg}`}>
            <div className="flex items-start gap-3">
              {getCategoryIcon(insight.category)}
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">What We Found</h3>
                <p className="text-gray-700">{insight.description}</p>
              </div>
            </div>
          </div>

          {/* Business Context */}
          {getBusinessContext().map((context, idx) => (
            <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                {context.title}
              </h4>
              <p className="text-sm text-blue-800 mb-2">{context.description}</p>
              <p className="text-xs text-blue-700 italic">{context.impact}</p>
            </div>
          ))}

          {/* Recommended Actions */}
          {insight.recommendation && (
            <div className="bg-purple-50 border-l-4 border-purple-500 rounded-r-lg p-4">
              <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Recommended Actions
              </h3>
              <p className="text-purple-800">{insight.recommendation}</p>
            </div>
          )}

          {/* Data Quality Indicator */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Insight Quality</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-600 mb-1">Confidence Score</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        insight.confidence_score >= 85 ? 'bg-green-500' : 
                        insight.confidence_score >= 70 ? 'bg-yellow-500' : 'bg-orange-500'
                      }`}
                      style={{ width: `${insight.confidence_score}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-gray-900">{Math.round(insight.confidence_score)}%</span>
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Data Source</div>
                <div className="text-sm font-semibold text-gray-900">
                  {isMLPowered ? `${insight.metadata?.model_name || 'Analytics Model'}` : 'Statistical Analysis'}
                </div>
                {insight.metadata?.model_accuracy && (
                  <div className="text-xs text-gray-600">{insight.metadata.model_accuracy} accuracy</div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3">
            {insight.related_sku && (
              <button
                onClick={handleViewSKU}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View SKU Analysis
              </button>
            )}
            {retailerName && (
              <button
                onClick={handleViewRetailer}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 rounded-lg font-medium transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View {retailerName} Dashboard
              </button>
            )}
            {!acknowledged ? (
              <button
                onClick={handleAcknowledge}
                disabled={isAcknowledging}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAcknowledging ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Acknowledging...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Got It
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={onClose}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium"
              >
                <CheckCircle className="w-4 h-4" />
                Acknowledged
              </button>
            )}
          </div>

          {/* Metadata (for transparency) */}
          <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
            <div className="grid grid-cols-2 gap-2">
              <div>Generated: {insight.generated_at ? new Date(insight.generated_at).toLocaleDateString() : 'N/A'}</div>
              <div>Type: {insight.insight_type}</div>
              <div>Source: {insight.source}</div>
              <div>ID: {insight.id.substring(0, 8)}...</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
